import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import useAuth from '@contexts/useAuth';
import { getFeatureFlags } from '@api/featureFlags';
import { FeatureFlagClientContext, type FeatureFlagClient } from './FeatureFlagClientContext';

/**
 * Config-backed feature flags (GovCloud). Reads the evaluated gates from
 * `GET /api/v1/feature-flags` so the backend profile is the single source of
 * truth and no Statsig SDK is loaded or contacted. Gates read `false` while the
 * fetch is in flight or on error (fail-closed), matching `useFeatureGate`.
 */
export const ConfigFeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { data, isFetched } = useQuery({
    queryKey: ['feature-flags', user?.id],
    queryFn: getFeatureFlags,
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  // No user => nothing gated renders anyway, so treat as resolved rather than
  // leaving route gates spinning on a query that never runs.
  const ready = !user || isFetched;

  const ffClient = useMemo<FeatureFlagClient>(
    () => ({
      checkGate: (name) => data?.[name] ?? false,
      isReady: () => ready,
    }),
    [data, ready],
  );

  return (
    <FeatureFlagClientContext.Provider value={ffClient}>
      {children}
    </FeatureFlagClientContext.Provider>
  );
};
